
/**
 * Find the right insertion point for block-level pagination elements.
 * When a break node is inside a <p> or other inline container, inserting
 * block DIVs inside it causes the browser to create anonymous block boxes
 * with extra spacing that overflows the page. This walks up from the node
 * to find the outermost inline ancestor so pagination elements can be
 * inserted before it as sibling block elements.
 */
export const getBlockInsertionPoint = (node) => {
  let target = node
  while (target.parentNode && /^(P|SPAN|A|STRONG|EM|B|I|U|S|LABEL)$/.test(target.parentNode.tagName)) {
    target = target.parentNode
  }
  return target
}

export const isRectEmpty = (rect) => rect.top === 0 && rect.bottom === 0 && rect.left === 0 && rect.right === 0

export const getTextRect = (node, pos = 0) => {
  const range = document.createRange()
  range.setStart(node, pos)
  range.setEnd(node, pos)
  const rect = range.getBoundingClientRect()
  if (isRectEmpty(rect)) { // safari fix
    const span = document.createElement('span')
    span.innerHTML = '&#8203;'
    if (pos === 0) {
      node.parentNode.insertBefore(span, node)
    } else {
      range.insertNode(span)
    }
    const spanRect = span.getBoundingClientRect()
    const parent = span.parentNode
    parent.removeChild(span)
    parent.normalize()
    return spanRect
  }
  return rect
}


export const binarySearchNextLine = (node, bottomBoundary, begin, textWindow) => {
  const max = node.textContent.length
  const end = Math.max(0, Math.min(begin + textWindow, max))
  const realWindow = end - begin
  const rect = getTextRect(node, end)
  if (realWindow <= 1) {
    if (!node.parentNode) {
      return null
    } else if (end <= 1 && ((node.parentNode.nodeName.toUpperCase() === 'LI') || (node.parentNode.nodeName.toUpperCase() === 'A'))){ // LI fix
      if (rect.bottom < bottomBoundary)
        return null
      const newNode = document.createElement('span')
      if (node.parentNode.previousSibling) {
        node.parentNode.previousSibling.appendChild(newNode)
        return {node: newNode, rect: rect, position: 0}
      } else if (node.parentNode.parentNode.className.indexOf('field-content' >= 0)) {
        return {node: node.parentNode.parentNode, rect: node.parentNode.parentNode.getBoundingClientRect()}
      } else {
        node.parentNode.parentNode.insertBefore(node.parentNode, newNode)
        return {node: newNode, react: rect, position: 0}
      }
    }

    const range = document.createRange()
    if (end > 0) {// firefox fix
      range.setStart(node, end - 1)
      range.setEnd(node, Math.min(end + 1, node.textContent.length))
    }
    const nonCollapsedRect = range.getBoundingClientRect()
    const endTextRect = getTextRect(node, end - 1)
    if (end > 0 && (endTextRect.bottom >= bottomBoundary ||
      (nonCollapsedRect.left < rect.left && nonCollapsedRect.bottom >= bottomBoundary))) // firefox fix
    {
      return {
        position: end - 1,
        node: node,
        rect: isRectEmpty(nonCollapsedRect) ? endTextRect : nonCollapsedRect
      }
    } else if (rect.bottom >= bottomBoundary) {
      return {position: end, node: node, rect}
    } else if (node.textContent.length > end) {
      return {position: end + 1, node: node, rect: getTextRect(node, end + 1)}
    } else {
      return null
    }
  } else {
    if (rect.bottom <= bottomBoundary) {
      if (textWindow === realWindow) {
        return binarySearchNextLine(node, bottomBoundary, begin + textWindow, Math.ceil(textWindow / 2))
      } else {
        return null
      }
    } else {
      return binarySearchNextLine(node, bottomBoundary, begin, Math.ceil(textWindow / 2))
    }
  }
}

export const treeWalker = (element) =>
  document.createTreeWalker(
    element,
    NodeFilter.SHOW_ALL,
    (e) => {
      if(e.nodeType === Node.TEXT_NODE){
        return NodeFilter.FILTER_ACCEPT
      } else {
        return e.childNodes.length ? NodeFilter.FILTER_SKIP : NodeFilter.FILTER_ACCEPT
      }
    },
    false
  )
export const treeWalkerTr = (element) =>
  document.createTreeWalker(
    element,
    NodeFilter.SHOW_ALL,
    (e) => {
      if (e.nodeType === Node.TEXT_NODE || e.nodeName.toUpperCase() === 'TR') {
        return NodeFilter.FILTER_ACCEPT
      } else {
        return e.childNodes.length ? NodeFilter.FILTER_SKIP : NodeFilter.FILTER_ACCEPT
      }
    },
    false
  )

const isInvisibleText = (text) => /^[\u200B\uFEFF\u00AD\u200C\u200D\u2060\s]*$/.test(text)

export const getTextLine = (element, bottom,useTreeWalkerOnlyText = false) => {
  const textWalker = useTreeWalkerOnlyText ? treeWalker(element, NodeFilter.SHOW_ALL) : treeWalkerTr(element, NodeFilter.SHOW_ALL)
  while (textWalker.nextNode()) {
    const current = textWalker.currentNode
    if (current.nodeType === Node.TEXT_NODE) {
      if (current.parentNode?.offsetHeight && current.parentNode?.offsetWidth) {
        // Skip text nodes that contain only invisible characters (zero-width spaces, etc.)
        // Their rendered position is misleading when sharing a line box with images,
        // causing the break to be placed at the text baseline instead of the image boundary
        if (isInvisibleText(current.textContent)) {
          continue
        }
        const length = current.textContent.length
        const result = binarySearchNextLine(current, bottom, 0, length)
        if (result) {
          return result
        }
      }
    } else {
      const rect = current.getBoundingClientRect()
      if (rect.bottom > bottom) {
        // Return parent + child index so the insertion uses range.insertNode
        // (same path as text breaks). Without this, getBlockInsertionPoint
        // walks up past <p>, inserting the page break before ALL content
        // in the <p> and leaving only the field label on the page.
        const parent = current.parentNode
        const childIndex = Array.from(parent.childNodes).indexOf(current)
        return {node: parent, position: childIndex, rect}
      }
    }
  }
  return null
}


export const getEmptySpace = (size,{className = "page-filler"} = {}) => {
  if (size <= 0) {
    const div = document.createElement('div')
    div.style.height = '0'
    div.style.minHeight = '0'
    div.className = "remove-on-clear"
    return div
  }
  const div = document.createElement('div')
  if (className) {
    div.className = className
  }
  div.style.height = size + 'px'
  return div
}

export const getPageBreak = (page, totalPages, pagePrefix = "", skipCounter) => {
  const div = document.createElement('div')
  const span = document.createElement('span')
  if (!skipCounter) {
    span.innerHTML = pagePrefix + `Page ${page+1} / ${totalPages+1}`
  }
  span.className = 'page-counter'
  div.appendChild(span)
  div.className = 'page-break'

  const clearBordersTop = document.createElement('div')
  const clearBordersBottom = document.createElement('div')
  clearBordersTop.className = 'clear-border clear-border-top'
  clearBordersBottom.className = 'clear-border clear-border-bottom'
  div.appendChild(clearBordersTop)
  div.appendChild(clearBordersBottom)

  return div
}

export const getLineHeight = (contents) => {
  const content = Array.prototype.slice.call(contents).find(el => el.textContent)
  if (!content)
    return 0
  const textWalker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT, null, false)
  const text = textWalker.nextNode()
  return getTextRect(text)?.height || 18
}

export const clearPagination = (container) =>
{
  container.classList.remove("ready-for-print")
  Array.prototype.slice.call(container.querySelectorAll('.page-filler,.page-break,.report-header-fix-container,.remove-on-clear'))
    .forEach(e => e.parentNode.removeChild(e))
  Array.prototype.slice.call(container.querySelectorAll('.has-broken'))
    .forEach(e => e.className = "always-break-here")
  Array.prototype.slice.call(container.querySelectorAll('.addendums-last-page-filler'))
    .forEach(e => e.style.height = "0")
  container.normalize()
}

export const getLastDiff = (separators) =>
{
  if (!separators.length) return 0
  return separators[separators.length - 1].diff
}
export const getAddendumAction = (node) => {
  return (node.closest ? node : node.parentNode).closest('.addendum-container').querySelector(".addendum-page-action")
}

export const getLastPageFiller = (node) => {
  return (node.closest ? node : node.parentNode).closest('.addendum-container').querySelector(".addendums-last-page-filler")
}

/**
 * Utilitários
 */
const isForcedBreak = node => node.classList.contains("always-break-here");
const isFieldContentBlock = node => node.classList.contains("field-content-block");
const shouldBreakIfSmall = node => node.classList.contains("next-page-if-min-small");

/**
 * Busca o próximo nó candidato a quebra
 */
function findBreakCandidate(nodes, contentBottom) {
  const index = nodes.findIndex(el =>
    el.getBoundingClientRect().bottom > contentBottom || isForcedBreak(el)
  );

  return {
    index,
    node: index >= 0 ? nodes[index] : null
  };
}

/**
 * Aplica quebra forçada (classe always-break-here)
 */
function applyForcedBreak(node, contentBottom) {
  const bottom = node.getBoundingClientRect().top;
  const filler = document.createElement("div");
  filler.style.height = (contentBottom - bottom - 1) + "px";
  filler.className = "remove-on-clear";
  node.parentNode.insertBefore(filler, node);
  node.classList.add("has-broken", "NODE-TO-BREAK");
  node.classList.remove("always-break-here");

  return {
    bottom: contentBottom - 1,
    result: { node }
  };
}

/**
 * Busca linha de texto para quebra dentro de um nó
 */
function tryGetTextLine(node, contentBottom, onlyText = false) {
  const candidate = [0, -51, -102].map(offset=> getTextLine(node, contentBottom + offset, onlyText))
    .reduce((acc,curr) => acc || curr)
  if (!candidate) { return null}
  if (candidate?.position !== undefined) {
    return {
      bottom: candidate.rect.top,
      result: { node: candidate.node, position: candidate.position }
    };
  }
  // fallback: quebra no topo do nó
  return {
    bottom: candidate.node.getBoundingClientRect().top,
    result: { node: candidate.node }
  };
}

/**
 * Resolve como quebrar (forçado, bloco pequeno, texto, etc.)
 */
function resolveBreak(node, contentBottom, opts, [nodes,index]) {
  const { minBreakSize, minContentSizeToBreak } = opts;

  if (isForcedBreak(node)) {
    return applyForcedBreak(node, contentBottom);
  } else if ( // Caso: bloco pequeno ou muito colado ao limite
    node.offsetHeight < minContentSizeToBreak ||
    node.getBoundingClientRect().top + minBreakSize > contentBottom
  ) {
    if (isFieldContentBlock(node) || shouldBreakIfSmall(node)) {
      return {
        bottom: node.getBoundingClientRect().top,
        result: { node }
      };
    } else {
      const parent = node.closest(".NODE-TO-BREAK");
      let hasNextNode = nodes.slice(index)
        .findIndex(el => el.getBoundingClientRect().bottom > contentBottom || el.className.indexOf("always-break-here") >= 0)
      if (hasNextNode) {
        return {
          bottom: node.parentNode.getBoundingClientRect().top,
          result: {node: parent}
        }
      }
    }
  }

  // Caso padrão: tenta quebrar dentro do texto
  return tryGetTextLine(node, contentBottom);
}
const getClosestElement = (node) => {
  if (node && node.nodeType === Node.ELEMENT_NODE) {
    return node
  }
  return node.parentElement
}
/**
 * Ajuste extra: evitar quebrar dentro de linhas grandes de tabela
 */
function adjustForLargeRow(node, bottom, contentBottom, result, opts) {
  const {fakeHeaderSpacing = 0} = opts
  const diff = contentBottom - bottom;
  if (diff > 200 && !isFieldContentBlock(node)) {
    const candidate = tryGetTextLine(node, contentBottom, true);

    if (candidate?.result?.node) {
      const breakNode = candidate.result.node;
      const tr = (breakNode.nodeType === 3 ? breakNode.parentNode : breakNode).closest("tr");

      if (tr) {
        const cellBreaks = Array.from(tr.cells).flatMap(cell => tryGetTextLine(cell, contentBottom, true) || []);
        if (cellBreaks.length > 0) {
          const firstCellDiff = contentBottom - cellBreaks[0].bottom
          cellBreaks.slice(1).forEach(({result,bottom}) => {
            const diff = contentBottom - bottom - firstCellDiff //+ fakeHeaderSpacing
            const emptySpaces = [
              getEmptySpace(fakeHeaderSpacing,{className: "hidden-until-ready fake-header-spacing remove-on-clear"}),
              getEmptySpace(1,{className: "hidden-until-ready fake-page-break remove-on-clear"}),
              diff >= 1 && getEmptySpace(diff,{className: "remove-on-clear"}),
              getEmptySpace(firstCellDiff,{className: "hidden-until-ready remove-on-clear"})
            ].filter(Boolean)
            if (result.position) {
              const range = document.createRange()
              range.setStart(result.node, result.position)
              range.setEnd(result.node, result.position)
              emptySpaces.forEach(emptySpace => range.insertNode(emptySpace))
            } else {
              emptySpaces.reverse().forEach(emptySpace => result.node.parentNode.insertBefore(emptySpace, result.node))
            }
          })
          return cellBreaks[0]
        }
        // debugger;
      }
    }
    return tryGetTextLine(node, contentBottom, true);
  }
  return { bottom, result };
}

/**
 * Função principal
 */
export function getSeparators(nodes, top, opts, lastDiff, firstPage = false) {
  const {
    contentPageHeight,
    contentLastPageHeight,
    canBeLastPage,
    lastPageIsCover
  } = opts;
  let contentBottom = top + contentPageHeight - lastDiff;

  // 1. Encontrar candidato
  let { index, node } = findBreakCandidate(nodes, contentBottom);

  // Se não há candidato
  if (index < 0) {
    if (canBeLastPage) {
      const contentLastPageBottom = top + contentLastPageHeight - lastDiff;
      const lastPageBreakCandidate = findBreakCandidate(nodes, contentLastPageBottom);
      if (lastPageBreakCandidate.index >= 0) {
        ({index,node} = lastPageBreakCandidate);
        contentBottom = contentLastPageBottom
      } else {
        return { top, separators: [] };
      }
    } else {
      return { top, separators: [] };
    }
  }

  // 2. Resolver como quebrar
  let { bottom, result } = resolveBreak(node, contentBottom, opts, [nodes,index]);

  // 3. Ajuste extra para tabelas longas
  ({ bottom, result } = adjustForLargeRow(node, bottom, contentBottom, result, opts));

  ({bottom, result} = adjustForEmptyHeadingInPage(node,bottom,result,opts.minBreakSize));

  // 4. Recursão
  const diff = contentBottom - bottom;
  const { top: lastTop, separators: next } = getSeparators(
    nodes.slice(index),
    contentBottom,
    opts,
    diff
  );

  // 5. Se nada coube, tenta novamente com altura da última página
  if (
    next.length === 0 &&
    contentLastPageHeight !== contentPageHeight &&
    !firstPage &&
    !lastPageIsCover
  ) {
    return getSeparators(nodes, top, { ...opts, contentPageHeight: contentLastPageHeight }, lastDiff);
  }

  return {
    top: lastTop,
    separators: [{ ...result, diff }, ...next]
  };
}

const getBoundingClientRectForTextOrElement = (node) => {
  if (node && node.nodeType === Node.ELEMENT_NODE) {
    return getClosestElement(node).getBoundingClientRect()
  }
  return getTextRect(node);
}

const adjustForEmptyHeadingInPage = (node,bottom,result, minBreakSize) => {
  if (shouldBreakIfSmall(node) &&
    result?.node &&
    node.getBoundingClientRect().top + minBreakSize > bottom
  ) {
    return {
      bottom: node.getBoundingClientRect().top,
      result: { node }
    };
  }
  return {bottom, result};
}

export const getImagePosition = (img,pageTops,left) => {
  const {top:imgTop,left:imgLeft} = img.getBoundingClientRect()
  const index = pageTops.findIndex(top => top > imgTop)
  const formatPos = (pageIndex) => ({y:imgTop - pageTops[pageIndex],x:imgLeft - left,page:pageIndex})
  if (index < 0) {
    return formatPos(pageTops.length - 1)
  }
  return formatPos(index - 1)
}


export const fixLiRenderBug = (container) => {
  const isFirstNonTextNode = (li,filler) => {
    const walker = document.createTreeWalker(li, NodeFilter.SHOW_ALL);
    let node, isBeforeText = false;

    while ((node = walker.nextNode())) {
      if (node === filler) {
        return true
      }
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '') {
        return false
      }
    }
  }
  [...container.querySelectorAll("li .page-filler")].forEach(filler => {
    const li = filler.closest("li")
    if (isFirstNonTextNode(li, filler)) {
      li.prepend(filler)
    }
  })
}